/**
 * Servicio para obtener datos del ticker de Clarosports
 * Maneja peticiones al endpoint de datos completos del ticker
 */

import axios from 'axios';
import { API_CORE_URL_RELATIVE } from "../constantes/constantes";

const BASE_TICKER = "/api/core-clarosports/v2/ticker";

/**
 * Obtiene datos completos del ticker para todas las ligas seleccionadas
 * 
 * Para cada liga seleccionada retorna:
 * - Información de la liga
 * - Tabla de posiciones actual
 * - Últimos 10 resultados
 * - Próximos 10 partidos
 * 
 * @async
 * @returns {Promise<Object>} Objeto con propiedades:
 *   - statusCode {number} Código de estado HTTP
 *   - data {Array<TickerLeagueData>} Array de datos del ticker por liga
 *   - message {string} Mensaje de error (si aplica)
 * 
 * @example
 * const response = await getTickerData();
 * if (response.statusCode === 200) {
 *   response.data.forEach(tickerData => {
 *     console.log(tickerData.league.name); // Nombre de la liga
 *     console.log(tickerData.standing);    // Tabla de posiciones
 *     console.log(tickerData.results);     // Últimos 10 partidos
 *     console.log(tickerData.upcoming);    // Próximos 10 partidos
 *   });
 * }
 * 
 * Estructura de respuesta:
 * [
 *   {
 *     league: {
 *       id: string,
 *       name: string,
 *       know_name: string,
 *       sport: string,
 *       sport_code: string,
 *       display_order: number,
 *       country: string
 *     },
 *     standing: object | null,  // Estructura varía por deporte
 *     results: Array,           // Últimos 10 partidos
 *     upcoming: Array           // Próximos 10 partidos
 *   },
 *   ...
 * ]
 * 
 * Cache: 60 segundos (Cache-Control: public, max-age=60)
 */
export async function getTickerData() {
  let endpoint = `${API_CORE_URL_RELATIVE}${BASE_TICKER}/data`;

  try {
    const { data, status } = await axios.get(endpoint, {
      timeout: 25000,
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json; charset=utf-8',
      },
    });

    return { statusCode: status, data };
  } catch (error) {
    console.error('Error en la petición getTickerData:', error.message || error);

    const status = error.response?.status || 500;
    const message = error.response?.data?.message || 'Error al obtener datos del ticker';
    
    return { statusCode: status, data: [], message };
  }
}

/**
 * Obtiene datos del ticker con reintentos automáticos
 * Útil para manejo de errores transitorios
 * 
 * @async
 * @param {number} maxRetries - Número máximo de reintentos (default: 3)
 * @param {number} delayMs - Milisegundos de espera entre reintentos (default: 1000)
 * @returns {Promise<Object>} Mismo formato que getTickerData()
 * 
 * @example
 * const response = await getTickerDataWithRetry(2, 500);
 */
export async function getTickerDataWithRetry(maxRetries = 3, delayMs = 1000) {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await getTickerData();
      if (response.statusCode === 200) {
        return response;
      }
      lastError = response;
    } catch (error) {
      lastError = { statusCode: 500, data: [], message: error.message };
    }

    // Esperar antes de reintentar (excepto en el último intento)
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return lastError || { statusCode: 500, data: [], message: 'Error después de múltiples reintentos' };
}